import { BcsStruct, TypeTagSerializer, bcs } from "@safecoin/safe/bcs";
import { normalizeSafeAddress, normalizeStructTag } from "@safecoin/safe/utils";
import { isArgument } from "@safecoin/safe/transactions";
//#region src/contracts/utils/index.ts
const MOVE_STDLIB_ADDRESS = normalizeSafeAddress("0x1");
const SAFE_FRAMEWORK_ADDRESS$1 = normalizeSafeAddress("0x2");
function getPureBcsSchema(typeTag) {
	const parsedTag = typeof typeTag === "string" ? TypeTagSerializer.parseFromStr(typeTag) : typeTag;
	if ("u8" in parsedTag) return bcs.U8;
	else if ("u16" in parsedTag) return bcs.U16;
	else if ("u32" in parsedTag) return bcs.U32;
	else if ("u64" in parsedTag) return bcs.U64;
	else if ("u128" in parsedTag) return bcs.U128;
	else if ("u256" in parsedTag) return bcs.U256;
	else if ("address" in parsedTag) return bcs.Address;
	else if ("bool" in parsedTag) return bcs.Bool;
	else if ("vector" in parsedTag) {
		const type = getPureBcsSchema(parsedTag.vector);
		return type ? bcs.vector(type) : null;
	} else if ("struct" in parsedTag) {
		const structTag = parsedTag.struct;
		const pkg = normalizeSafeAddress(structTag.address);
		if (pkg === MOVE_STDLIB_ADDRESS) {
			if ((structTag.module === "ascii" || structTag.module === "string") && structTag.name === "String") return bcs.String;
			if (structTag.module === "option" && structTag.name === "Option") {
				const inner = structTag.typeParams[0];
				const type = inner ? getPureBcsSchema(inner) : null;
				return type ? bcs.option(type) : null;
			}
		}
		if (pkg === SAFE_FRAMEWORK_ADDRESS$1 && structTag.module === "object" && (structTag.name === "ID" || structTag.name === "UID")) return bcs.Address;
	}
	return null;
}
function normalizeMoveArguments(args, argTypes, parameterNames) {
	const argLen = Array.isArray(args) ? args.length : Object.keys(args).length;
	if (parameterNames && argLen !== parameterNames.length) throw new Error(`Invalid number of arguments, expected ${parameterNames.length}, got ${argLen}`);
	const normalizedArgs = [];
	let index = 0;
	for (const argType of argTypes) {
		if (argType === "0x2::clock::Clock") {
			normalizedArgs.push((tx) => tx.object.clock());
			continue;
		}
		if (argType === "0x2::random::Random") {
			normalizedArgs.push((tx) => tx.object.random());
			continue;
		}
		if (argType === "0x2::deny_list::DenyList") {
			normalizedArgs.push((tx) => tx.object.denyList());
			continue;
		}
		if (argType === "0x2::accumulator::AccumulatorRoot") {
			normalizedArgs.push((tx) => tx.object("0xacc"));
			continue;
		}
		if (argType === "0x3::safe_system::SafeSystemState") {
			normalizedArgs.push((tx) => tx.object.system());
			continue;
		}
		let arg;
		if (Array.isArray(args)) {
			if (index >= args.length) throw new Error(`Invalid number of arguments, expected at least ${index + 1}, got ${args.length}`);
			arg = args[index];
		} else {
			if (!parameterNames) throw new Error(`Expected arguments to be passed as an array`);
			const name = parameterNames[index];
			arg = name !== void 0 && Object.prototype.hasOwnProperty.call(args, name) ? args[name] : void 0;
			if (arg === void 0) throw new Error(`Parameter ${name} is required`);
		}
		index += 1;
		if (typeof arg === "function" || isArgument(arg)) {
			normalizedArgs.push(arg);
			continue;
		}
		const bcsType = argType === null ? null : getPureBcsSchema(argType);
		if (bcsType) {
			const bytes = bcsType.serialize(arg);
			normalizedArgs.push((tx) => tx.pure(bytes));
			continue;
		}
		if (typeof arg === "string") {
			normalizedArgs.push((tx) => tx.object(arg));
			continue;
		}
		throw new Error(`Invalid argument ${stringify(arg)} for type ${argType}`);
	}
	return normalizedArgs;
}
const HAS_PHANTOM_REGEX = /phantom [A-Za-z_$][A-Za-z0-9_$]*/;
function splitTopLevelTypeArgs(inner) {
	const parts = [];
	let depth = 0;
	let current = "";
	for (const char of inner) {
		if (char === "," && depth === 0) {
			parts.push(current.trim());
			current = "";
			continue;
		}
		if (char === "<") depth++;
		if (char === ">") depth--;
		current += char;
	}
	if (current) parts.push(current.trim());
	return parts;
}
function buildTypeTag(name, options) {
	const lt = name.indexOf("<");
	const base = lt === -1 ? name : name.slice(0, lt);
	if (base.split("::").length !== 3) throw new Error(`${name} is not a top-level Move type`);
	let result = name;
	if (options?.typeArguments) {
		const baked = lt === -1 ? [] : splitTopLevelTypeArgs(name.slice(lt + 1, -1));
		const supplied = options.typeArguments.map((arg) => {
			if (typeof arg === "string") return arg;
			if (arg && typeof arg.serialize === "function" && typeof arg.name === "string") return arg.name;
			throw new Error(`Invalid type argument ${stringify(arg)}`);
		});
		if (supplied.length !== baked.length) throw new Error(`Expected ${baked.length} type arguments for ${base}, got ${supplied.length}`);
		result = supplied.length === 0 ? base : `${base}<${supplied.join(", ")}>`;
	}
	if (HAS_PHANTOM_REGEX.test(result)) throw new Error(options?.typeArguments ? `A type argument contains an unfilled phantom parameter in ${result}` : `Missing type arguments for ${result}`);
	if (options?.package) {
		const [, ...rest] = result.split("::");
		result = [options.package, ...rest].join("::");
	}
	if (!HAS_PHANTOM_REGEX.test(result) && !/[@/]/.test(result)) TypeTagSerializer.parseFromStr(result);
	return result;
}
async function resolveBuiltTypeTag(name, options) {
	const { client, ...rest } = options;
	const { type } = await client.core.mvr.resolveType({ type: buildTypeTag(name, rest) });
	return normalizeStructTag(type);
}
var MoveStruct = class extends BcsStruct {
	/**
	* Build the type tag for this struct.
	*
	* `typeArguments` is the full positional list, in Move declaration order, and
	* is required when the struct has unfilled phantom parameters. The result may
	* contain MVR names: those are valid in transaction `typeArguments`, but for
	* queries or comparisons against on-chain data use `resolveTypeTag` instead.
	*/
	typeTag(...args) {
		return buildTypeTag(this.name, args[0]);
	}
	/**
	* Build the type tag for this struct, then resolve any MVR names through the
	* client (using its configured overrides and the MVR API) and return the
	* normalized, address-only form suitable for queries and comparisons against
	* on-chain data.
	*/
	async resolveTypeTag(options) {
		return resolveBuiltTypeTag(this.name, options);
	}
	async get({ objectId, ...options }) {
		const [res] = await this.getMany({
			...options,
			objectIds: [objectId]
		});
		if (!res) throw new Error(`No object found for id ${objectId}`);
		return res;
	}
	async getMany({ client, ...options }) {
		return (await client.core.getObjects({
			...options,
			include: {
				...options.include,
				content: true
			}
		})).objects.map((obj) => {
			if (obj instanceof Error) throw obj;
			return {
				...obj,
				json: this.parse(obj.content)
			};
		});
	}
};
function stringify(val) {
	if (typeof val === "object") return JSON.stringify(val, (_key, value) => typeof value === "bigint" ? value.toString() : value);
	if (typeof val === "bigint") return val.toString();
	return val;
}
//#endregion
export { MoveStruct, getPureBcsSchema, normalizeMoveArguments };

//# sourceMappingURL=index.mjs.map